import { useCallback, useRef } from 'react';
import { SILENCE_DBFS, useAudioLevel } from 'hooks/useAudioLevel';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, BoxProps } from '@mui/material';

const Meter = styled('div')({
  '--vu-meter-value': '0%',
  display: 'flex',
  justifyContent: 'flex-end',
  height: '0.75rem',
  overflow: 'hidden',
  background: 'linear-gradient(90deg, #ffe60a 12%, #00ff06 25%, #0f0 80%, #f00 94%)',
  borderRadius: '8px',
});

const Bar = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: 'calc(100% - var(--vu-meter-value))',
  height: '0.75rem',
  overflow: 'hidden',
  backgroundColor: '#c0c0c0',
});

const Label = styled('span')(({ theme }) => ({
  display: 'block',
  marginBottom: '0.375rem',
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

type VUMeterProps = {
  on: boolean;
  label?: string;
  className?: string;
  stream: MediaStream | null;
};

// The meter spans a quiet room (empty) to full scale (full).
const METER_FLOOR_DBFS = -60;
// Smoothing the amplitude rather than the dB shows a sound at once and fades it out, the look
// the analyser's own 0.85 smoothing gave the meter before the level hook switched to RMS.
const METER_SMOOTHING = 0.85;

const VUMeter = ({ stream, label, on, ...props }: BoxProps<'div', VUMeterProps>) => {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  const amplitudeRef = useRef(0);

  const updateVUMeter = useCallback(
    (dbfs: number) => {
      if (!ref.current || !on) {
        return;
      }
      const amplitude =
        dbfs <= SILENCE_DBFS
          ? 0
          : amplitudeRef.current * METER_SMOOTHING + 10 ** (dbfs / 20) * (1 - METER_SMOOTHING);
      amplitudeRef.current = amplitude;
      const smoothedDbfs = 20 * Math.log10(amplitude);
      const width = Math.round(
        Math.min(100, Math.max(0, (1 - smoothedDbfs / METER_FLOOR_DBFS) * 100))
      );
      ref.current.style.setProperty('--vu-meter-value', `${width}%`);
      ref.current.setAttribute('aria-valuenow', String(width));
    },
    [on]
  );

  useAudioLevel({
    stream,
    onChange: updateVUMeter,
  });

  return (
    <Box {...props}>
      {label && <Label>{label}</Label>}
      <Meter>
        <Bar
          ref={ref}
          role="progressbar"
          aria-label={t('vumeter.aria_label')}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </Meter>
    </Box>
  );
};

export default VUMeter;
